import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import useTracking from '@/hooks/useTracking';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  CheckCircle, XCircle, Star, Info, Zap, ShoppingBag, CreditCard, Rocket,
  Loader2, QrCode, Building, Wallet, Truck, Search, Shield, Clock,
  Package, ArrowRight, ChevronDown, ChevronUp, Users, BadgeCheck,
  Phone, Mail, MapPin, FileText, Copy, ExternalLink, Gift, Flame,
  CircleDot, Heart, Tag, Plus, Minus, TrendingUp, X, Landmark, Upload,
  Timer, Play, HelpCircle, ShieldCheck, MousePointerClick, Calendar, Video, User
} from 'lucide-react';
import { toast } from 'sonner';

const BULLET_ICON_MAP = {
  check: CheckCircle, x: XCircle, star: Star, info: Info, zap: Zap,
};
const CTA_ICON_MAP = {
  ShoppingBag, CreditCard, Zap, Rocket,
};

const PAYMENT_METHODS = [
  { value: 'qris', label: 'QRIS', icon: QrCode, desc: 'Scan QR — semua bank & e-wallet', group: 'qris' },
  { value: 'bni_va', label: 'BNI', icon: Building, desc: 'Virtual Account BNI', group: 'va' },
  { value: 'bri_va', label: 'BRI', icon: Building, desc: 'Virtual Account BRI', group: 'va' },
  { value: 'cimb_niaga_va', label: 'CIMB', icon: Building, desc: 'Virtual Account CIMB Niaga', group: 'va' },
  { value: 'permata_va', label: 'Permata', icon: Building, desc: 'Virtual Account Permata', group: 'va' },
  { value: 'maybank_va', label: 'Maybank', icon: Building, desc: 'Virtual Account Maybank', group: 'va' },
  { value: 'dana', label: 'DANA', icon: Wallet, desc: 'E-Wallet DANA', group: 'ewallet' },
  { value: 'ovo', label: 'OVO', icon: Wallet, desc: 'E-Wallet OVO', group: 'ewallet' },
  { value: 'shopeepay', label: 'ShopeePay', icon: Wallet, desc: 'E-Wallet ShopeePay', group: 'ewallet' },
  { value: 'gopay', label: 'GoPay', icon: Wallet, desc: 'E-Wallet GoPay', group: 'ewallet' },
  { value: 'bank_transfer', label: 'Transfer Manual', icon: Landmark, desc: 'Transfer ke rekening bank penjual', group: 'bank_transfer' },
];

export default function LandingCheckout() {
  const { slug } = useParams();
  const subdomain = useStoreSubdomain();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [paymentTab, setPaymentTab] = useState('qris');

  // Bank transfer proof state
  const [proofForm, setProofForm] = useState({ bank_name: '', account_name: '', amount: '', transfer_date: '', proof_url: '', notes: '' });
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [proofSubmitted, setProofSubmitted] = useState(false);

  // Shipping state
  const [shippingInfo, setShippingInfo] = useState(null);
  const [codSettings, setCodSettings] = useState(null);
  const [destSearch, setDestSearch] = useState('');
  const [destResults, setDestResults] = useState([]);
  const [destSearching, setDestSearching] = useState(false);
  const [selectedDest, setSelectedDest] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [shippingCosts, setShippingCosts] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);

const [form, setForm] = useState(() => {
    const saved = localStorage.getItem('user');
    let init = { customer_name: '', customer_email: '', customer_phone: '', customer_address: '', payment_method: 'qris', is_cod: false, notes: '', voucher_code: '', affiliate_code: '', reseller_code: '', is_dropship: false, dropship_sender_name: '', dropship_sender_phone: '' };
    if (saved) {
      try {
        const u = JSON.parse(saved);
        if (u.role === 'customer') { init.customer_name = u.name || ''; init.customer_email = u.email || ''; }
      } catch {}
    }
    return init;
  });

  // Reseller state
  const [resellerPrices, setResellerPrices] = useState(null);
  const [resellerTierName, setResellerTierName] = useState('');

  // Order bump state
  const [orderBumps, setOrderBumps] = useState([]);
  const [selectedBumps, setSelectedBumps] = useState([]);
  const [bundleSuggestions, setBundleSuggestions] = useState([]);
  const [selectedBundleSugg, setSelectedBundleSugg] = useState([]);

  // Upsell/Downsell state
  const [upsellOffers, setUpsellOffers] = useState([]);
  const [upsellState, setUpsellState] = useState('idle');
  const [currentFunnel, setCurrentFunnel] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [upsellOrderResult, setUpsellOrderResult] = useState(null);

  // Voucher state
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [discount, setDiscount] = useState(0);

  // Loyalty points state
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyPointsInput, setLoyaltyPointsInput] = useState('');
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyPointsUsed, setLoyaltyPointsUsed] = useState(0);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);

  // Affiliate state
  const [affiliateName, setAffiliateName] = useState('');
  const [affiliateValid, setAffiliateValid] = useState(null);

  // Quantity state
  const [quantity, setQuantity] = useState(1);

  // Countdown state
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });

  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  // Floating CTA visibility
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Webinar registration state
  const [webinarRegistering, setWebinarRegistering] = useState(false);
  const [webinarResult, setWebinarResult] = useState(null);

  // Event registration state
  const [eventRegistering, setEventRegistering] = useState(false);
  const [eventResult, setEventResult] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [eventQuantity, setEventQuantity] = useState(1);

  // Booking state
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [bookingAvailability, setBookingAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingEndDate, setBookingEndDate] = useState('');
  const [bookingQuantity, setBookingQuantity] = useState(1);
  const [bookingNotes, setBookingNotes] = useState('');
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const formRef = useRef(null);

  const page = data?.landing_page;
  const product = data?.product;
  const store = data?.store;
  const bundle = data?.bundle;
  const isPhysical = bundle ? false : product?.type === 'physical';
  const isWebinar = product?.type === 'webinar';
  const isEvent = product?.type === 'event';
  const isBooking = product?.type === 'booking';
  const webinarData = product?.webinar;
  const webinarSessions = data?.webinar_sessions || [];
  const eventData = product?.event;
  const eventTicketTypes = data?.event_ticket_types || [];
  const bookingService = data?.booking_service || product?.booking_service;

  // Helpers for display — works with both product and bundle
  const displayName = bundle ? bundle.name : product?.name;
  const displayImage = bundle ? bundle.images?.[0] : product?.images?.[0];
  const displayPrice = bundle ? (bundle.final_price || bundle.bundle_price || 0) : (product?.price || 0);

  const { trackEvent } = useTracking(store?.store_extras);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  useEffect(() => {
    const fetchLP = async () => {
      try {
        const res = await api.get(`/store/${subdomain}/landing/${slug}`);
        setData(res.data);
        try { const s = await api.get(`/store/${subdomain}/shipping-info`); setShippingInfo(s.data); } catch {}
        try { const c = await api.get(`/store/${subdomain}/cod-settings`); setCodSettings(c.data); } catch {}
        // Set bump products directly from LP response
        const lp = res.data?.landing_page;
        if (lp?.enable_order_bump && res.data?.bump_products?.length > 0) {
          setOrderBumps(res.data.bump_products.map(p => ({
            id: p.id,
            product_id: p.id,
            headline: p.name,
            description: '',
            image_url: p.images?.[0] || '',
            bump_price: p.sale_price || p.price,
            product: p,
          })));
        }
        // Set bundle suggestions from LP response
        if (res.data?.bundle_suggestions?.length > 0) {
          setBundleSuggestions(res.data.bundle_suggestions);
        }
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    fetchLP();

    // Pre-fill phone & address from customer profile if logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        if (u.role === 'customer' && localStorage.getItem('token')) {
          api.get('/customer/profile').then(res => {
            const c = res.data.customer;
            if (c) setForm(f => ({ ...f, customer_phone: f.customer_phone || c.phone || '', customer_address: f.customer_address || c.address || '' }));
          }).catch(() => {});
          api.get('/customer/addresses').then(res => {
            const def = (res.data.addresses || []).find(a => a.is_default);
            if (def) setForm(f => ({ ...f, customer_address: f.customer_address || [def.address, def.city, def.province].filter(Boolean).join(', '), customer_phone: f.customer_phone || def.phone || '' }));
          }).catch(() => {});
        }
      } catch {}
    }

    // Get affiliate code from URL or saved referral
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || localStorage.getItem(`affiliate_ref_${subdomain}`) || '';
    if (ref) {
      setForm(f => ({ ...f, affiliate_code: ref }));
      api.get(`/store/${subdomain}/validate-affiliate`, { params: { code: ref } })
        .then(res => {
          if (res.data.valid) { setAffiliateName(res.data.name); setAffiliateValid(true); }
          else setAffiliateValid(false);
        })
        .catch(() => setAffiliateValid(false));
    }
  }, [subdomain, slug]);

  // Fetch booking availability when booking type
  useEffect(() => {
    if (!isBooking || !subdomain || !product?.id || !calMonth) return;
    fetch(`/api/store/${subdomain}/booking-services/${product.id}/availability?month=${calMonth}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setBookingAvailability(d))
      .catch(() => {});
  }, [isBooking, subdomain, product?.id, calMonth]);

  useEffect(() => {
    if (product) trackEvent('ViewContent', { content_name: product.name, value: product.price });
    else if (bundle) trackEvent('ViewContent', { content_name: bundle.name, value: bundle.final_price || bundle.bundle_price });
  }, [product, bundle, trackEvent]);

  // Inject head HTML blocks (meta, style, tracking scripts)
  useEffect(() => {
    if (!page?.custom_html_blocks) return;
    const headBlocks = page.custom_html_blocks.filter(b => b.position === 'head' && b.html);
    if (headBlocks.length === 0) return;
    const container = document.createElement('div');
    container.id = 'lp-head-scripts';
    headBlocks.forEach(b => {
      const range = document.createRange();
      range.selectNode(document.head);
      const frag = range.createContextualFragment(b.html);
      container.appendChild(frag);
    });
    document.head.appendChild(container);
    return () => { const el = document.getElementById('lp-head-scripts'); if (el) el.remove(); };
  }, [page]);

  // Countdown timer
  useEffect(() => {
    if (!page?.enable_countdown || !page?.countdown_end) return;
    const endDate = new Date(page.countdown_end).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = endDate - now;
      if (diff <= 0) { setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }); return; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [page?.enable_countdown, page?.countdown_end]);

  // Floating CTA scroll listener
  useEffect(() => {
    if (!page?.enable_floating_cta && page?.enable_floating_cta !== undefined) return;
    const onScroll = () => setShowFloatingCta(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [page?.enable_floating_cta]);

  // YouTube embed URL helper
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regExp);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const searchDestination = useCallback(async () => {
    if (!destSearch || destSearch.length < 2) { toast.error('Ketik minimal 2 karakter'); return; }
    setDestSearching(true);
    try {
      const res = await api.get(`/store/shipping/destination-search?search=${encodeURIComponent(destSearch)}&limit=10`);
      const results = res.data?.data || res.data?.rajaongkir?.results || [];
      setDestResults(Array.isArray(results) ? results : []);
      if (!results || results.length === 0) toast.info('Tidak ditemukan lokasi');
    } catch { toast.error('Gagal mencari lokasi'); }
    finally { setDestSearching(false); }
  }, [destSearch]);

  const calculateShipping = useCallback(async () => {
    if (!selectedDest) { toast.error('Pilih tujuan pengiriman'); return; }
    if (!selectedCourier) { toast.error('Pilih kurir'); return; }
    setShippingLoading(true);
    try {
      const destId = selectedDest.subdistrict_id || selectedDest.district_id || selectedDest.city_id || selectedDest.id || '';
      // Calculate weight: use bundle products' total weight if bundle, else product weight
      const bundleWeight = data?.bundle_products?.reduce((sum, bp) => sum + ((bp.product?.weight || 500) * (bp.quantity || 1)), 0) || 500;
      const weight = (bundle ? bundleWeight : (product?.weight || 500)) * quantity;
      const res = await api.post(`/store/${subdomain}/shipping/calculate`, {
        tenant_subdomain: subdomain, destination: String(destId), weight, courier: selectedCourier, price: subtotal,
      });
      setShippingCosts(res.data.costs || []);
      if (res.data.costs?.length > 0) setSelectedShipping(res.data.costs[0]);
    } catch { toast.error('Gagal menghitung ongkir'); }
    finally { setShippingLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDest, selectedCourier, product, subdomain, quantity]);

  const codFee = form.is_cod ? (selectedShipping?.cod_fee || codSettings?.cod_fee || 0) : 0;
  const bumpTotal = selectedBumps.reduce((a, b) => a + (b.bump_price ?? b.product?.price ?? 0), 0);
  const bundleSuggTotal = selectedBundleSugg.reduce((a, b) => a + (b.final_price || 0), 0);
  const subtotal = bundle ? (bundle.final_price || 0) * quantity : (product?.price || 0) * quantity;
  const shippingCost = isPhysical
    ? (shippingInfo?.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min)
      ? 0 : (shippingInfo?.flat_rate > 0 ? shippingInfo.flat_rate : (selectedShipping?.cost || 0))
    : 0;
  const total = subtotal + bumpTotal + bundleSuggTotal + shippingCost + codFee - discount - loyaltyDiscount;

  // Validate voucher
  const validateVoucher = async () => {
    if (!voucherInput.trim()) return;
    setVoucherLoading(true);
    try {
      const res = await api.post(`/store/${subdomain}/validate-voucher?code=${encodeURIComponent(voucherInput.trim())}`);
      if (res.data.valid) {
        setVoucherApplied(res.data.voucher);
        setForm(f => ({ ...f, voucher_code: voucherInput.trim() }));
        const v = res.data.voucher;
        let disc = 0;
        if (v.discount_type === 'percentage') {
          disc = Math.round(subtotal * v.discount_value / 100);
          if (v.max_discount > 0) disc = Math.min(disc, v.max_discount);
        } else {
          disc = Math.min(v.discount_value, subtotal);
        }
        if (v.min_purchase > 0 && subtotal < v.min_purchase) {
          toast.error(`Minimum pembelian Rp ${fmt(v.min_purchase)}`);
          return;
        }
        setDiscount(disc);
        toast.success(`Voucher "${v.code}" berhasil! Diskon Rp ${fmt(disc)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kode voucher tidak valid');
      setVoucherApplied(null);
      setDiscount(0);
    } finally { setVoucherLoading(false); }
  };

  const removeVoucher = () => {
    setVoucherApplied(null); setVoucherInput(''); setDiscount(0);
    setForm(f => ({ ...f, voucher_code: '' }));
  };

  // Loyalty points
  const fetchLoyaltyInfo = async () => {
    if (!form.customer_email) return;
    try {
      const r = await api.get(`/store/${subdomain}/loyalty-info?email=${encodeURIComponent(form.customer_email)}`);
      if (r.data.enabled) setLoyaltyInfo(r.data);
    } catch { }
  };

  const applyLoyaltyPoints = async () => {
    const pts = parseInt(loyaltyPointsInput) || 0;
    if (pts <= 0) { toast.error('Masukkan jumlah poin'); return; }
    setLoyaltyLoading(true);
    try {
      const r = await api.post(`/store/${subdomain}/loyalty-validate?email=${encodeURIComponent(form.customer_email)}&points_to_redeem=${pts}&order_total=${subtotal - discount}`);
      if (r.data.valid) {
        setLoyaltyPointsUsed(r.data.points_to_redeem);
        setLoyaltyDiscount(r.data.discount);
        toast.success(`${r.data.points_to_redeem} poin diterapkan! Diskon Rp ${fmt(r.data.discount)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal validasi poin');
    } finally { setLoyaltyLoading(false); }
  };

  const removeLoyaltyPoints = () => {
    setLoyaltyPointsUsed(0); setLoyaltyDiscount(0); setLoyaltyPointsInput('');
  };

  const toggleBump = (bump) => {
    const isSelected = selectedBumps.find(b => b.id === bump.id);
    if (isSelected) {
      setSelectedBumps(selectedBumps.filter(b => b.id !== bump.id));
    } else {
      setSelectedBumps([...selectedBumps, bump]);
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Webinar registration handler
  const handleWebinarRegister = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      return toast.error('Nama dan email wajib diisi');
    }
    setWebinarRegistering(true);
    try {
      const res = await api.post(`/store/${subdomain}/webinars/${product.id}/register`, {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        payment_method: form.payment_method,
      });
      setWebinarResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mendaftar webinar');
    } finally {
      setWebinarRegistering(false);
    }
  };

  // Event registration handler
  const handleEventRegister = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) return toast.error('Nama dan email wajib diisi');
    if (!selectedTicketType) return toast.error('Pilih tipe tiket');
    setEventRegistering(true);
    try {
      const res = await fetch(`/api/store/${subdomain}/events/${product.id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_type_id: selectedTicketType.id,
          quantity: eventQuantity,
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          payment_method: form.payment_method,
        }),
      });
      const rData = await res.json();
      if (!res.ok) throw new Error(rData.detail || 'Registrasi gagal');
      setEventResult(rData);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message || 'Gagal mendaftar event');
    } finally {
      setEventRegistering(false);
    }
  };

  // Booking handler
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) return toast.error('Nama dan email wajib diisi');
    if (!selectedDate) return toast.error('Pilih tanggal booking');
    const bMode = bookingService?.booking_mode;
    if (['hourly', 'session'].includes(bMode) && !selectedSlot) return toast.error('Pilih slot waktu');
    setBookingSubmitting(true);
    try {
      const body = {
        service_id: product.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: bookingNotes,
        payment_method: form.payment_method,
        booking_date: selectedDate,
        quantity: bookingQuantity,
      };
      if (bMode === 'daily' && bookingEndDate) body.end_date = bookingEndDate;
      if (selectedSlot) body.slot_id = selectedSlot.id;

      const res = await fetch(`/api/store/${subdomain}/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const rData = await res.json();
      if (!res.ok) throw new Error(rData.detail || 'Booking gagal');
      setBookingResult(rData);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message || 'Gagal membuat booking');
    } finally {
      setBookingSubmitting(false);
    }
  };

  // Booking calendar helpers
  const bookingMode = bookingService?.booking_mode;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso; } };
  const navMonth = (dir) => {
    const [y, m] = calMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const getCalDays = () => {
    const [y, m] = calMonth.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };
  const getDayAvail = (day) => {
    if (!day || !bookingAvailability?.dates) return null;
    const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
    return bookingAvailability.dates.find(d => d.date === dateStr);
  };
  const todayStr = new Date().toISOString().split('T')[0];
  const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  const [calY, calM] = calMonth.split('-').map(Number);
  const slotsForDate = bookingAvailability?.slots?.filter(s => s.date === selectedDate && !s.is_blocked && s.available > 0) || [];

  // Compute booking total price based on mode
  const bookingUnitPrice = selectedSlot?.price_override ?? bookingService?.price ?? 0;
  const bookingDays = (() => {
    if (bookingMode !== 'daily' || !selectedDate) return 1;
    if (!bookingEndDate) return 1;
    const start = new Date(selectedDate);
    const end = new Date(bookingEndDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff);
  })();
  const bookingTotalPrice = bookingMode === 'quantity'
    ? bookingUnitPrice * bookingQuantity
    : bookingUnitPrice * (bookingMode === 'daily' ? bookingDays : 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      return toast.error('Nama dan email wajib diisi');
    }
    setSubmitting(true);
    trackEvent('InitiateCheckout', { value: total, content_name: displayName });
    try {
      const destId = selectedDest?.id || selectedDest?.subdistrict_id || selectedDest?.district_id || selectedDest?.city_id || '';
      const destLabel = selectedDest
        ? `${selectedDest.subdistrict_name || selectedDest.district_name || selectedDest.city_name || ''}, ${selectedDest.province_name || selectedDest.province || ''}` : '';
      const payload = {
        tenant_subdomain: subdomain, customer_name: form.customer_name, customer_email: form.customer_email,
        customer_phone: form.customer_phone, customer_address: form.customer_address,
        payment_method: form.is_cod ? 'cod' : form.payment_method, is_cod: form.is_cod, notes: form.notes,
        voucher_code: form.voucher_code, affiliate_code: form.affiliate_code,
        reseller_code: form.reseller_code, is_dropship: form.is_dropship,
        dropship_sender_name: form.dropship_sender_name, dropship_sender_phone: form.dropship_sender_phone,
        items: [
          ...(bundle
            ? [{ bundle_id: bundle.id, quantity }]
            : [{ product_id: product.id, quantity, name: product.name, price: product.price, type: product.type || 'physical' }]
          ),
          ...selectedBumps.map(b => ({ product_id: b.product_id, quantity: 1, is_bump: true })),
          ...selectedBundleSugg.map(b => ({ bundle_id: b.id, quantity: 1 }))
        ],
        shipping_cost: shippingCost, shipping_courier: selectedShipping?.courier || '',
        shipping_service: selectedShipping?.service || '', destination_id: String(destId),
        destination_label: destLabel, cod_fee: codFee,
        loyalty_points_to_redeem: loyaltyPointsUsed,
      };
      const res = await api.post('/store/checkout', payload);
      trackEvent('Purchase', { value: total, content_name: displayName, order_id: res.data.order_id });
      setSuccess(res.data);
      // Fetch upsell offers
      try {
        const orderItems = res.data?.order?.items || [];
        const purchasedIds = orderItems.map(i => i.product_id).filter(Boolean);
        const uniqueIds = [...new Set(purchasedIds)].join(',');
        if (uniqueIds) {
          const uRes = await api.get(`/store/${subdomain}/upsell-offers?product_ids=${uniqueIds}`);
          const offers = uRes.data.upsell_offers || [];
          setUpsellOffers(offers);
          if (offers.length > 0) {
            setCurrentFunnel(offers[0]);
            setUpsellState('showing_upsell');
          }
        }
      } catch (ue) { console.error('Upsell fetch error:', ue); }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memproses pesanan');
    } finally { setSubmitting(false); }
  };

  // ─── LOADING ───
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-blue-500 mx-auto" />
          <p className="text-gray-500 text-sm animate-pulse">Memuat halaman...</p>
        </div>
      </div>
    );
  }

  if (!data || !page || (!product && !bundle)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-sm mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Package className="h-10 w-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">Halaman Tidak Ditemukan</h2>
          <p className="text-gray-500 text-sm">Landing page yang Anda cari tidak tersedia atau sudah dihapus.</p>
        </div>
      </div>
    );
  }

  const color = page.primary_color || '#2D6CDF';
  const CtaIcon = CTA_ICON_MAP[page.button_icon] || ShoppingBag;

  // darken/lighten helpers
  const colorLight = `${color}12`;
  const colorMedium = `${color}25`;

  // ─── WEBINAR REGISTRATION SUCCESS ───
  if (webinarResult) {
    const isFree = !webinarResult.payment_url;
    const reg = webinarResult.registration || {};
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-indigo-50">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${color}, #8B5CF6)` }} />
            <div className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto bg-violet-100">
                  <CheckCircle className="h-14 w-14 text-violet-500" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
                {isFree ? 'Pendaftaran Berhasil!' : 'Pendaftaran Diterima!'}
              </h2>
              <p className="text-gray-500 mb-6">
                {isFree
                  ? `Terima kasih, ${form.customer_name}! Anda berhasil terdaftar.`
                  : `Silakan selesaikan pembayaran untuk mengkonfirmasi pendaftaran.`}
              </p>

              {/* Webinar Info */}
              <div className="bg-violet-50 rounded-xl p-4 mb-6 border border-violet-200 text-left space-y-2">
                <div className="flex items-center gap-3">
                  {displayImage && <img src={displayImage} alt={displayName} className="w-14 h-14 rounded-lg object-cover" />}
                  <div>
                    <p className="font-semibold text-sm text-gray-800">{displayName}</p>
                    <p className="text-xs text-violet-500 font-medium flex items-center gap-1"><Video className="h-3 w-3" /> Webinar</p>
                  </div>
                </div>
                {webinarData?.speaker_name && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><User className="h-3 w-3 text-violet-400" /> Speaker: <strong>{webinarData.speaker_name}</strong></p>
                )}
                {webinarData?.scheduled_at && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><Calendar className="h-3 w-3 text-violet-400" /> {new Date(webinarData.scheduled_at).toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' })}</p>
                )}
              </div>

              {/* Payment button for paid webinar */}
              {webinarResult.payment_url && (
                <a href={webinarResult.payment_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-white font-bold text-base shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
                  style={{ backgroundColor: color }}>
                  <CreditCard className="h-5 w-5" /> Bayar Sekarang — Rp {fmt(product?.price || 0)}
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              )}

              {/* Meeting URL for free webinar */}
              {isFree && webinarData?.meeting_url && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left">
                  <p className="text-sm font-medium text-emerald-800 mb-2 flex items-center gap-1.5"><Video className="h-4 w-4" /> Link Meeting</p>
                  <a href={webinarData.meeting_url} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline break-all">{webinarData.meeting_url}</a>
                  <button onClick={() => { navigator.clipboard.writeText(webinarData.meeting_url); toast.success('Link disalin'); }}
                    className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <Copy className="h-3 w-3" /> Salin link
                  </button>
                </div>
              )}

              {/* Order info for paid webinar */}
              {webinarResult.order && (
                <div className="bg-gray-50 rounded-xl p-4 mt-4 border border-gray-100 text-left">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">No. Order</p>
                  <p className="text-sm font-bold font-mono text-gray-800">{webinarResult.order.order_number}</p>
                </div>
              )}

              <div className="mt-6 flex items-start gap-2 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Detail pendaftaran juga dikirim ke email <strong>{form.customer_email}</strong> dan WhatsApp Anda.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── EVENT REGISTRATION SUCCESS ───
  if (eventResult) {
    const isPaid = !!(eventResult.order || eventResult.payment_url || eventResult.bank_accounts);
    const isBankTransfer = eventResult.bank_accounts?.length > 0;
    const orderNum = eventResult.order?.order_number || eventResult.registration?.order_number || '';
    const ticketPrice = selectedTicketType?.price || 0;
    const eventTotal = ticketPrice * eventQuantity;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-indigo-50">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${color}, #8B5CF6)` }} />
            <div className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto bg-violet-100">
                  <CheckCircle className="h-14 w-14 text-violet-500" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
                {isPaid ? 'Pesanan Dibuat!' : 'Registrasi Berhasil!'}
              </h2>
              <p className="text-gray-500 mb-6">
                {isPaid ? 'Silakan selesaikan pembayaran untuk mendapatkan tiket Anda.' : `Tiket Anda sudah siap, ${form.customer_name}!`}
              </p>

              <div className="bg-violet-50 rounded-xl p-4 mb-6 border border-violet-200 text-left space-y-2">
                <div className="flex items-center gap-3">
                  {displayImage && <img src={displayImage} alt={displayName} className="w-14 h-14 rounded-lg object-cover" />}
                  <div>
                    <p className="font-semibold text-sm text-gray-800">{displayName}</p>
                    <p className="text-xs text-violet-500 font-medium flex items-center gap-1"><Calendar className="h-3 w-3" /> Event</p>
                  </div>
                </div>
                {selectedTicketType && (
                  <p className="text-xs text-gray-600">Tiket: <strong>{selectedTicketType.name}</strong> x{eventQuantity}</p>
                )}
                {eventData?.start_date && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><Calendar className="h-3 w-3 text-violet-400" /> {new Date(eventData.start_date).toLocaleDateString('id-ID', { dateStyle: 'long' })}</p>
                )}
                {eventData?.venue_name && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><MapPin className="h-3 w-3 text-violet-400" /> {eventData.venue_name}</p>
                )}
                {orderNum && (
                  <div className="pt-2 border-t border-violet-100">
                    <p className="text-xs text-gray-500">No. Order: <span className="font-mono font-bold">{orderNum}</span></p>
                  </div>
                )}
                <div className="flex justify-between pt-1">
                  <span className="text-sm font-medium text-gray-700">Total</span>
                  <span className="text-lg font-bold" style={{ color }}>{eventTotal > 0 ? `Rp ${fmt(eventTotal)}` : 'Gratis'}</span>
                </div>
              </div>

              {isPaid && isBankTransfer && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left mb-4">
                  <p className="text-sm font-medium text-blue-800 mb-3">🏦 Transfer ke salah satu rekening:</p>
                  <div className="space-y-2">
                    {eventResult.bank_accounts.map((acc, i) => (
                      <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-blue-900 text-sm">{acc.bank_name}</span>
                          <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Disalin'); }} className="text-xs text-blue-600 flex items-center gap-1"><Copy className="h-3 w-3" /> Salin</button>
                        </div>
                        <p className="font-mono text-lg tracking-wider">{acc.account_number}</p>
                        <p className="text-xs text-gray-500">a.n. {acc.account_holder}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                    <strong>Transfer tepat:</strong> Rp {fmt(eventTotal)} agar verifikasi lebih cepat
                  </div>
                </div>
              )}

              {isPaid && !isBankTransfer && eventResult.payment_url && (
                <a href={eventResult.payment_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-white font-bold text-base shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] mb-4"
                  style={{ backgroundColor: color }}>
                  <CreditCard className="h-5 w-5" /> Bayar Sekarang — Rp {fmt(eventTotal)}
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              )}

              {!isPaid && eventResult.tickets?.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left mb-4">
                  <p className="text-sm font-medium text-emerald-800 mb-2">🎟️ Tiket Anda</p>
                  {eventResult.tickets.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-lg p-2 border mb-1">
                      <div>
                        <p className="font-mono text-xs font-bold">{t.ticket_number}</p>
                        <p className="text-xs text-slate-500">{t.attendee_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-start gap-2 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Detail registrasi juga dikirim ke email <strong>{form.customer_email}</strong> dan WhatsApp Anda.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── BOOKING SUCCESS ───
  if (bookingResult) {
    const isBankTransfer = bookingResult.bank_accounts?.length > 0;
    const bkgTotal = bookingResult.total_price || 0;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${color}, #3B82F6)` }} />
            <div className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto bg-blue-100">
                  <CheckCircle className="h-14 w-14 text-blue-500" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
                {bkgTotal > 0 ? 'Booking Dibuat!' : 'Booking Berhasil!'}
              </h2>
              <p className="text-gray-500 mb-6">
                {bkgTotal > 0 ? 'Silakan lakukan pembayaran untuk mengkonfirmasi booking.' : 'Booking Anda sudah dikonfirmasi!'}
              </p>

              <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200 text-left space-y-2">
                <div className="flex items-center gap-3">
                  {displayImage && <img src={displayImage} alt={displayName} className="w-14 h-14 rounded-lg object-cover" />}
                  <div>
                    <p className="font-semibold text-sm text-gray-800">{displayName}</p>
                    <p className="text-xs text-blue-500 font-medium flex items-center gap-1"><Calendar className="h-3 w-3" /> Booking</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">No. Booking: <span className="font-mono font-bold">{bookingResult.booking_number}</span></p>
                <p className="text-xs text-gray-600 flex items-center gap-1.5"><Calendar className="h-3 w-3 text-blue-400" /> {fmtDate(selectedDate)}</p>
                {selectedSlot && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><Clock className="h-3 w-3 text-blue-400" /> {selectedSlot.time_start} - {selectedSlot.time_end}</p>
                )}
                <div className="flex justify-between pt-2 border-t border-blue-100">
                  <span className="text-sm font-medium text-gray-700">Total</span>
                  <span className="text-lg font-bold" style={{ color }}>{bkgTotal > 0 ? `Rp ${fmt(bkgTotal)}` : 'Gratis'}</span>
                </div>
              </div>

              {isBankTransfer && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left mb-4">
                  <p className="text-sm font-medium text-blue-800 mb-3">🏦 Transfer ke salah satu rekening:</p>
                  <div className="space-y-2">
                    {bookingResult.bank_accounts.map((acc, i) => (
                      <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-blue-900 text-sm">{acc.bank_name}</span>
                          <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Disalin'); }} className="text-xs text-blue-600 flex items-center gap-1"><Copy className="h-3 w-3" /> Salin</button>
                        </div>
                        <p className="font-mono text-lg tracking-wider">{acc.account_number}</p>
                        <p className="text-xs text-gray-500">a.n. {acc.account_holder}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                    <strong>Transfer tepat:</strong> Rp {fmt(bkgTotal)} agar verifikasi lebih cepat
                  </div>
                </div>
              )}

              {!isBankTransfer && bookingResult.payment_url && (
                <a href={bookingResult.payment_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-white font-bold text-base shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] mb-4"
                  style={{ backgroundColor: color }}>
                  <CreditCard className="h-5 w-5" /> Bayar Sekarang — Rp {fmt(bkgTotal)}
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              )}

              <div className="mt-4 flex items-start gap-2 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Detail booking juga dikirim ke email <strong>{form.customer_email}</strong> dan WhatsApp Anda.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── SUCCESS SCREEN ───
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-lg mx-auto px-4 py-8">
          {/* Success card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Top gradient bar */}
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${color}, ${color}99)` }} />

            <div className="p-8 text-center">
              {/* Animated check */}
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: `${color}15` }}>
                  <CheckCircle className="h-14 w-14" style={{ color }} />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Pesanan Berhasil!</h2>
              <p className="text-gray-500 mb-6">Terima kasih, <span className="font-semibold text-gray-700">{form.customer_name}</span></p>

              {/* Invoice detail */}
              {success.invoice_number && (
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">No. Invoice</p>
                  <p className="text-lg font-bold text-gray-800 font-mono">{success.invoice_number}</p>
                </div>
              )}

              {/* Order summary in success */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100 text-left space-y-2">
                <div className="flex items-center gap-3">
                  {displayImage && (
                    <img src={displayImage} alt={displayName} className="w-14 h-14 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-800 truncate">{displayName}</p>
                    {bundle && <p className="text-xs text-purple-500 font-medium">Bundle</p>}
                    <p className="text-xs text-gray-500">Qty: {quantity}</p>
                  </div>
                  <p className="font-bold text-sm" style={{ color }}>Rp {fmt(subtotal)}</p>
                </div>
                {shippingCost > 0 && (
                  <div className="flex justify-between text-xs text-gray-500 pt-2 border-t">
                    <span>Ongkir</span><span>Rp {fmt(shippingCost)}</span>
                  </div>
                )}
                {codFee > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Biaya COD</span><span>Rp {fmt(codFee)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600">
                    <span>Diskon{voucherApplied ? ` (${voucherApplied.code})` : ''}</span><span>-Rp {fmt(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-sm pt-2 border-t">
                  <span>Total Pembayaran</span><span style={{ color }}>Rp {fmt(total)}</span>
                </div>
              </div>

              {/* Payment button */}
              {success.payment_url && success.payment_provider !== 'bank_transfer' && (
                <a href={success.payment_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-white font-bold text-base shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
                  style={{ backgroundColor: color }}>
                  <CreditCard className="h-5 w-5" />
                  Bayar Sekarang
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              )}

              {/* Bank Transfer Info + Proof Upload */}
              {success.payment_provider === 'bank_transfer' && (
                <div className="space-y-4 text-left">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-blue-800 mb-3">&#127974; Transfer ke salah satu rekening:</p>
                    <div className="space-y-2">
                      {(success.bank_accounts || []).map((acc, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-blue-900 text-sm">{acc.bank_name}</span>
                            <button onClick={() => { navigator.clipboard.writeText(acc.account_number); toast.success('Disalin'); }} className="text-xs text-blue-600 flex items-center gap-1"><Copy className="h-3 w-3" /> Salin</button>
                          </div>
                          <p className="font-mono text-lg tracking-wider">{acc.account_number}</p>
                          <p className="text-xs text-gray-500">a.n. {acc.account_holder}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                      <strong>Transfer tepat:</strong> Rp {fmt(total)} agar verifikasi lebih cepat
                    </div>
                  </div>

                  {!proofSubmitted ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                      <p className="text-sm font-medium text-gray-700">&#128206; Konfirmasi Pembayaran</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-xs text-gray-500">Bank Pengirim</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="BCA, BNI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} /></div>
                        <div><label className="text-xs text-gray-500">Nama Pengirim</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} /></div>
                        <div><label className="text-xs text-gray-500">Jumlah Transfer</label><input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder={String(total)} value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} /></div>
                        <div><label className="text-xs text-gray-500">Tanggal Transfer</label><input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} /></div>
                      </div>
                      <div><label className="text-xs text-gray-500">URL Bukti Transfer (opsional)</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://..." value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} /></div>
                      <button
                        className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ backgroundColor: color }}
                        disabled={proofSubmitting || !proofForm.bank_name || !proofForm.account_name || !proofForm.amount}
                        onClick={async () => {
                          setProofSubmitting(true);
                          try {
                            await api.post(`/store/${subdomain}/payment-confirmation`, {
                              order_number: success.order?.order_number, customer_email: form.customer_email,
                              bank_name: proofForm.bank_name, account_name: proofForm.account_name,
                              amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
                              proof_url: proofForm.proof_url, notes: proofForm.notes
                            });
                            setProofSubmitted(true); toast.success('Konfirmasi terkirim!');
                          } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          finally { setProofSubmitting(false); }
                        }}>
                        {proofSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Kirim Konfirmasi Pembayaran
                      </button>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                      <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                      <p className="font-medium text-emerald-800">Konfirmasi Terkirim!</p>
                      <p className="text-xs text-emerald-600 mt-1">Penjual akan memverifikasi pembayaran Anda</p>
                    </div>
                  )}
                </div>
              )}

              {/* Upsell/Downsell Offer */}
              {currentFunnel && upsellState !== 'idle' && upsellState !== 'done' && (() => {
                const isUpsell = upsellState === 'showing_upsell';
                const offerProduct = isUpsell ? currentFunnel.upsell_product : currentFunnel.downsell_product;
                const specialPrice = isUpsell ? currentFunnel.upsell_price : currentFunnel.downsell_price;
                const headline = isUpsell ? currentFunnel.upsell_headline : currentFunnel.downsell_headline;
                const description = isUpsell ? currentFunnel.upsell_description : currentFunnel.downsell_description;
                const imageUrl = isUpsell ? currentFunnel.upsell_image_url : currentFunnel.downsell_image_url;
                if (!offerProduct) return null;
                const originalPrice = offerProduct.sale_price || offerProduct.price;
                const finalPrice = specialPrice != null ? specialPrice : originalPrice;
                return (
                  <div className="mt-6 border-2 rounded-xl p-5 space-y-4" style={{ borderColor: color, background: `${color}08` }}>
                    <div className="text-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {isUpsell ? 'Penawaran Spesial!' : 'Penawaran Terakhir!'}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 mt-2">{headline}</h3>
                    </div>
                    <div className="flex items-center gap-3">
                      {(imageUrl || offerProduct.images?.[0]) && (
                        <img src={imageUrl || offerProduct.images[0]} alt={offerProduct.name} className="w-20 h-20 object-cover rounded-lg border" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-800">{offerProduct.name}</p>
                        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-lg font-bold" style={{ color }}>Rp {fmt(finalPrice)}</span>
                          {specialPrice != null && specialPrice < originalPrice && (
                            <span className="text-xs text-gray-400 line-through">Rp {fmt(originalPrice)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      className="w-full py-3 rounded-xl text-white font-bold text-sm shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{ backgroundColor: color }}
                      disabled={upsellLoading}
                      onClick={async () => {
                        setUpsellLoading(true);
                        try {
                          const r = await api.post(`/store/${subdomain}/upsell-accept`, {
                            original_order_id: success.order?.id,
                            funnel_id: currentFunnel.id,
                            offer_type: isUpsell ? 'upsell' : 'downsell',
                            payment_method: form.is_cod ? 'qris' : form.payment_method,
                          });
                          setUpsellOrderResult(r.data);
                          setUpsellState('done');
                          if (r.data.payment_url) window.open(r.data.payment_url, '_blank');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Gagal memproses');
                        } finally { setUpsellLoading(false); }
                      }}
                    >
                      {upsellLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                      Ya, Tambahkan ke Pesanan!
                    </button>
                    <button
                      className="w-full text-xs text-gray-400 hover:text-gray-600 py-1 flex items-center justify-center gap-1"
                      onClick={() => {
                        if (isUpsell && currentFunnel.downsell_product_id && currentFunnel.downsell_product) {
                          setUpsellState('showing_downsell');
                        } else {
                          setUpsellState('done');
                        }
                      }}
                      disabled={upsellLoading}
                    >
                      <X className="h-3 w-3" /> Tidak, Terima Kasih
                    </button>
                  </div>
                );
              })()}

              {/* Upsell Accepted Confirmation */}
              {upsellOrderResult && upsellState === 'done' && (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-500" />
                    <div>
                      <p className="font-medium text-emerald-800 text-sm">Pesanan tambahan berhasil!</p>
                      <p className="text-xs text-emerald-600">Order #{upsellOrderResult.order?.order_number}</p>
                    </div>
                  </div>
                  {upsellOrderResult.payment_url && (
                    <a href={upsellOrderResult.payment_url} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-white font-bold text-sm shadow-lg"
                      style={{ backgroundColor: color }}>
                      <CreditCard className="h-4 w-4" />
                      Bayar Pesanan Tambahan — Rp {fmt(upsellOrderResult.order?.total)}
                    </a>
                  )}
                </div>
              )}

              {/* Info note */}
              <div className="mt-6 flex items-start gap-2 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Detail pembayaran juga dikirim ke email <strong>{form.customer_email}</strong>. Segera lakukan pembayaran sebelum batas waktu.
                </p>
              </div>

              {/* Link to Thank You page */}
              <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/store/${subdomain}/thank-you?order=${success.order?.order_number}&type=order`}>
                  <Button variant="outline" className="w-full sm:w-auto" style={{ borderColor: color, color }}>
                    <FileText className="h-4 w-4 mr-2" /> Lihat Halaman Pesanan
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Trust footer */}
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Transaksi Aman</span>
            <span>•</span>
            <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Terverifikasi</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN LANDING PAGE ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50">
      {page.seo_title && <title>{page.seo_title}</title>}

      {/* ═══════ HERO SECTION ═══════ */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}08 0%, ${color}15 50%, ${color}05 100%)` }}>
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10" style={{ background: color, filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10" style={{ background: color, filter: 'blur(60px)' }} />

        <div className="relative max-w-2xl mx-auto px-4 pt-8 pb-10">
          {/* Store badge */}
          {store?.name && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/80 backdrop-blur border border-gray-200 shadow-sm text-gray-600">
                <BadgeCheck className="h-3.5 w-3.5" style={{ color }} />
                {store.name}
              </div>
            </div>
          )}

          {/* Hero image */}
          {page.hero_image_url && (
            <div className="mb-6 rounded-2xl overflow-hidden shadow-xl border border-white/50">
              <img src={page.hero_image_url} alt={page.headline} className="w-full object-cover max-h-[400px]" />
            </div>
          )}

          {/* Headline */}
          <div className="text-center space-y-3 mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-gray-900">
              {page.headline || page.title}
            </h1>
            {page.subheadline && (
              <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
                {page.subheadline}
              </p>
            )}
          </div>

          {/* Quick CTA */}
          <div className="flex justify-center">
            <button onClick={scrollToForm}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-bold text-base shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: color }}>
              <CtaIcon className="h-5 w-5" />
              {page.button_text || 'Beli Sekarang'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Social Proof Badge */}
          {page.enable_social_proof && page.social_proof_text && (
            <div className="flex justify-center mt-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-sm text-sm font-medium text-gray-700">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                {page.social_proof_text}
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {page.enable_countdown && page.countdown_end && !countdown.expired && (
            <div className="mt-6">
              <div className="bg-white/90 backdrop-blur rounded-2xl border border-red-200 shadow-sm p-4 max-w-sm mx-auto text-center">
                {page.countdown_text && (
                  <p className="text-sm font-semibold text-red-600 mb-2 flex items-center justify-center gap-1.5">
                    <Timer className="h-4 w-4" /> {page.countdown_text}
                  </p>
                )}
                <div className="flex items-center justify-center gap-2">
                  {[
                    { val: countdown.days, label: 'Hari' },
                    { val: countdown.hours, label: 'Jam' },
                    { val: countdown.minutes, label: 'Mnt' },
                    { val: countdown.seconds, label: 'Dtk' },
                  ].map((t, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <div className="w-14 h-14 rounded-xl bg-red-500 text-white flex items-center justify-center text-xl font-extrabold shadow">
                        {String(t.val).padStart(2, '0')}
                      </div>
                      <span className="text-[10px] text-gray-500 mt-1 font-medium">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {page.enable_countdown && countdown.expired && (
            <div className="mt-6 text-center">
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-semibold border border-red-200">
                <Timer className="h-4 w-4" /> Penawaran telah berakhir
              </span>
            </div>
          )}

          {/* Trust badges row */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-green-500" /> Pembayaran Aman</span>
            <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-blue-500" /> Pengiriman Terpercaya</span>
            <span className="flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5 text-purple-500" /> Produk Original</span>
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 pb-12">

        {/* ═══════ BENEFITS / BULLET ITEMS ═══════ */}
        {page.bullet_items?.length > 0 && (
          <section className="mt-8">
            <div className="text-center mb-5">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Kenapa Harus Pilih Produk Ini?</h2>
              <div className="w-12 h-1 rounded-full mx-auto mt-2" style={{ backgroundColor: color }} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {page.bullet_items.map((b, i) => {
                const BIcon = BULLET_ICON_MAP[b.icon] || CheckCircle;
                return (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
                      <BIcon className="h-4.5 w-4.5" style={{ color }} />
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed pt-1.5">{b.text}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ═══════ CUSTOM HTML: after_hero ═══════ */}
        {(page.custom_html_blocks || []).filter(b => b.position === 'after_hero' && b.html).map((b, i) => (
          <section key={`html-ah-${i}`} className="mt-6" dangerouslySetInnerHTML={{ __html: b.html }} />
        ))}

        {/* ═══════ DESCRIPTION ═══════ */}
        {page.description_html && (
          <section className="mt-8">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100">
              <div className="prose prose-sm sm:prose-base max-w-none text-gray-700 prose-headings:text-gray-900 prose-a:text-blue-600 prose-img:rounded-xl">
                <div dangerouslySetInnerHTML={{ __html: page.description_html }} />
              </div>
            </div>
          </section>
        )}

        {/* ═══════ VIDEO SECTION ═══════ */}
        {page.video_url && (() => {
          const embedUrl = getYoutubeEmbedUrl(page.video_url);
          if (!embedUrl) return null;
          return (
            <section className="mt-8">
              {page.video_title && (
                <div className="text-center mb-5">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 mb-3">
                    <Play className="h-3.5 w-3.5" /> Video
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{page.video_title}</h2>
                </div>
              )}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    src={embedUrl}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={page.video_title || 'Video'}
                  />
                </div>
              </div>
            </section>
          );
        })()}

        {/* ═══════ IMAGE GALLERY ═══════ */}
        {page.image_gallery?.length > 0 && (
          <section className="mt-8">
            <div className={`grid gap-3 ${page.image_gallery.length === 1 ? 'grid-cols-1' : page.image_gallery.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
              {page.image_gallery.map((url, i) => (
                <div key={i} className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
                  <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-40 sm:h-48 object-cover hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ═══════ PRODUCT / BUNDLE SHOWCASE ═══════ */}
        <section className="mt-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Image */}
            {displayImage && (
              <div className="relative">
                <img src={displayImage} alt={displayName} className="w-full h-60 sm:h-72 object-cover" />
                {!bundle && product?.discount_percent > 0 && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white bg-red-500 shadow">
                    -{product.discount_percent}%
                  </div>
                )}
                {bundle && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white bg-purple-500 shadow">
                    Bundle
                  </div>
                )}
                {isWebinar && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white bg-violet-500 shadow flex items-center gap-1">
                    <Video className="h-3 w-3" /> Webinar
                  </div>
                )}
                {isEvent && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white bg-violet-500 shadow flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Event
                  </div>
                )}
                {isBooking && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-500 shadow flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Booking
                  </div>
                )}
                {isPhysical && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur shadow text-gray-700 flex items-center gap-1">
                    <Package className="h-3 w-3" /> Produk Fisik
                  </div>
                )}
                {isWebinar && webinarData?.status && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur shadow text-gray-700 flex items-center gap-1">
                    {webinarData.status === 'live' ? <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /> : <Clock className="h-3 w-3" />}
                    {webinarData.status === 'upcoming' ? 'Akan Datang' : webinarData.status === 'live' ? 'LIVE' : webinarData.status === 'completed' ? 'Selesai' : webinarData.status}
                  </div>
                )}
              </div>
            )}

            <div className="p-5 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{displayName}</h3>

              {bundle && bundle.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{bundle.description}</p>
              )}
              {!bundle && product?.short_description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.short_description}</p>
              )}

              {/* Webinar Info */}
              {isWebinar && webinarData && (
                <div className="mb-4 space-y-3">
                  {/* Speaker */}
                  {webinarData.speaker_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <User className="h-4 w-4 text-violet-500" />
                      <span className="font-medium">Speaker:</span>
                      <span>{webinarData.speaker_name}</span>
                    </div>
                  )}
                  {/* Schedule */}
                  {webinarData.start_time && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4 text-violet-500" />
                      <span className="font-medium">Jadwal:</span>
                      <span>{new Date(webinarData.start_time).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}, {new Date(webinarData.start_time).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB</span>
                    </div>
                  )}
                  {/* Sessions */}
                  {webinarSessions?.length > 0 && (
                    <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
                      <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2">Sesi Webinar ({webinarSessions.length})</p>
                      <div className="space-y-2">
                        {webinarSessions.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">{i + 1}</span>
                            <div>
                              <p className="font-medium text-gray-800">{s.title}</p>
                              {s.description && <p className="text-xs text-gray-500">{s.description}</p>}
                              {s.duration_minutes && <p className="text-xs text-violet-600">{s.duration_minutes} menit</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Quota */}
                  {webinarData.max_participants > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-gray-600">Kuota Peserta</span>
                        <span className="font-bold text-gray-800">{webinarData.registration_count || 0} / {webinarData.max_participants}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(100, ((webinarData.registration_count || 0) / webinarData.max_participants) * 100)}%`, backgroundColor: color }} />
                      </div>
                      {(webinarData.max_participants - (webinarData.registration_count || 0)) <= 10 && (webinarData.max_participants - (webinarData.registration_count || 0)) > 0 && (
                        <p className="text-xs text-red-500 font-medium mt-1">Sisa {webinarData.max_participants - (webinarData.registration_count || 0)} slot!</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Event Info */}
              {isEvent && eventData && (
                <div className="mb-4 space-y-3">
                  {eventData.start_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4 text-violet-500" />
                      <span className="font-medium">Tanggal:</span>
                      <span>{new Date(eventData.start_date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                  )}
                  {eventData.venue_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <MapPin className="h-4 w-4 text-violet-500" />
                      <span className="font-medium">Venue:</span>
                      <span>{eventData.venue_name}{eventData.venue_city ? `, ${eventData.venue_city}` : ''}</span>
                    </div>
                  )}
                  {/* Ticket Types */}
                  {eventTicketTypes.filter(tt => tt.is_active !== false).length > 0 && (
                    <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
                      <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2">Tipe Tiket</p>
                      <div className="space-y-2">
                        {eventTicketTypes.filter(tt => tt.is_active !== false).map(tt => {
                          const remaining = tt.quota ? tt.quota - (tt.sold_count || 0) : null;
                          const soldOut = remaining !== null && remaining <= 0;
                          const isSelected = selectedTicketType?.id === tt.id;
                          return (
                            <button
                              key={tt.id}
                              disabled={soldOut}
                              onClick={() => { setSelectedTicketType(tt); setEventQuantity(1); }}
                              className={`w-full p-3 rounded-lg border text-left transition-all ${isSelected ? 'border-violet-500 bg-violet-100/50' : soldOut ? 'opacity-50' : 'hover:border-violet-300'}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-sm text-gray-800">{tt.name}</span>
                                <span className="font-bold text-sm" style={{ color }}>{tt.price > 0 ? `Rp ${fmt(tt.price)}` : 'GRATIS'}</span>
                              </div>
                              {tt.description && <p className="text-xs text-gray-500 mt-1">{tt.description}</p>}
                              {soldOut && <p className="text-xs text-red-500 font-medium mt-1">Sold Out</p>}
                              {remaining !== null && remaining > 0 && remaining <= 20 && (
                                <p className="text-xs text-orange-500 mt-1">Sisa {remaining} tiket</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {selectedTicketType && (
                        <div className="mt-3 flex items-center gap-3 pt-3 border-t border-violet-200">
                          <span className="text-xs font-medium text-gray-600">Jumlah:</span>
                          <div className="flex items-center border border-violet-300 rounded-lg overflow-hidden">
                            <button type="button" onClick={() => setEventQuantity(q => Math.max(1, q - 1))} className="w-8 h-8 flex items-center justify-center hover:bg-violet-100" disabled={eventQuantity <= 1}><Minus className="h-3 w-3" /></button>
                            <span className="w-10 text-center font-bold text-sm">{eventQuantity}</span>
                            <button type="button" onClick={() => setEventQuantity(q => q + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-violet-100"><Plus className="h-3 w-3" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Booking Info — Calendar & Slot Selection */}
              {isBooking && bookingService && (
                <div className="mb-4 space-y-3">
                  {bookingService.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <MapPin className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">Lokasi:</span>
                      <span>{bookingService.location}</span>
                    </div>
                  )}
                  {bookingService.operating_hours && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">Jam Operasional:</span>
                      <span>{bookingService.operating_hours}</span>
                    </div>
                  )}

                  {/* Calendar */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3">Pilih Tanggal</p>
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={() => navMonth(-1)} className="p-1 rounded hover:bg-blue-100"><ChevronDown className="h-4 w-4 rotate-90" /></button>
                      <span className="font-semibold text-sm text-gray-800">{monthNames[calM - 1]} {calY}</span>
                      <button type="button" onClick={() => navMonth(1)} className="p-1 rounded hover:bg-blue-100"><ChevronDown className="h-4 w-4 -rotate-90" /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(d => (
                        <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {getCalDays().map((day, i) => {
                        if (!day) return <div key={`e${i}`} />;
                        const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
                        const isPast = dateStr < todayStr;
                        const dayAvail = getDayAvail(day);
                        const isBlocked = dayAvail?.is_blocked;
                        const isFull = dayAvail && dayAvail.available !== undefined && dayAvail.available <= 0;
                        const isSel = dateStr === selectedDate;
                        const canSelect = !isPast && !isBlocked && !isFull;
                        return (
                          <button key={day} type="button" disabled={!canSelect}
                            onClick={() => { setSelectedDate(dateStr); setSelectedSlot(null); }}
                            className={`aspect-square rounded-lg text-xs font-medium transition-all relative
                              ${isSel ? 'bg-blue-600 text-white shadow-md' : ''}
                              ${!isSel && canSelect ? 'hover:bg-blue-100 text-gray-700' : ''}
                              ${isPast ? 'text-gray-300' : ''}
                              ${isBlocked ? 'bg-red-50 text-red-300 line-through' : ''}
                              ${isFull ? 'bg-gray-100 text-gray-300' : ''}
                              ${!isPast && !isBlocked && !isFull && dayAvail?.available > 0 ? 'ring-1 ring-green-300' : ''}
                            `}>
                            {day}
                          </button>
                        );
                      })}
                    </div>

                    {/* Time Slots for hourly/session */}
                    {selectedDate && ['hourly', 'session'].includes(bookingMode) && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs font-semibold text-blue-600 mb-2">Pilih Waktu — {fmtDate(selectedDate)}</p>
                        {slotsForDate.length === 0 ? (
                          <p className="text-xs text-gray-400">Tidak ada slot tersedia untuk tanggal ini.</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {slotsForDate.map(s => (
                              <button key={s.id} type="button" onClick={() => setSelectedSlot(s)}
                                className={`p-2 rounded-lg border text-xs text-left transition-all ${selectedSlot?.id === s.id ? 'border-blue-500 bg-blue-100' : 'hover:border-blue-300'}`}>
                                <p className="font-medium">{s.time_start} - {s.time_end}</p>
                                <p className="text-[10px] text-gray-500">{s.available} tersedia{s.label ? ` · ${s.label}` : ''}</p>
                                {s.price_override != null && <p className="text-[10px] font-medium text-orange-600">Rp {fmt(s.price_override)}</p>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Date range for daily mode */}
                    {selectedDate && bookingMode === 'daily' && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Tanggal Selesai (opsional)</label>
                        <Input type="date" min={selectedDate} value={bookingEndDate} onChange={e => setBookingEndDate(e.target.value)} className="h-9 text-sm" />
                      </div>
                    )}

                    {/* Quantity for quantity mode */}
                    {bookingMode === 'quantity' && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Jumlah</label>
                        <Input type="number" min={1} max={bookingService.capacity || 99} value={bookingQuantity}
                          onChange={e => setBookingQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="h-9 text-sm" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-4">
                {(isWebinar || isEvent) && displayPrice === 0 ? (
                  <span className="text-2xl sm:text-3xl font-extrabold text-green-600">Gratis</span>
                ) : (
                  <span className="text-2xl sm:text-3xl font-extrabold" style={{ color }}>
                    Rp {fmt(displayPrice)}
                    {isBooking && bookingMode === 'daily' && <span className="text-base font-medium text-gray-500">/hari</span>}
                    {isBooking && bookingMode === 'hourly' && <span className="text-base font-medium text-gray-500">/jam</span>}
                    {isBooking && bookingMode === 'session' && <span className="text-base font-medium text-gray-500">/sesi</span>}
                    {isBooking && bookingMode === 'quantity' && <span className="text-base font-medium text-gray-500">/unit</span>}
                  </span>
                )}
                {bundle && bundle.original_total > displayPrice && (
                  <span className="text-base text-gray-400 line-through">
                    Rp {fmt(bundle.original_total)}
                  </span>
                )}
                {!bundle && product?.original_price && product.original_price > product.price && (
                  <span className="text-base text-gray-400 line-through">
                    Rp {fmt(product.original_price)}
                  </span>
                )}
                {bundle && bundle.original_total > displayPrice && (
                  <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                    Hemat {Math.round((1 - displayPrice / bundle.original_total) * 100)}%
                  </span>
                )}
              </div>

              {/* Bundle items list */}
              {bundle && data?.bundle_products?.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Isi Paket:</p>
                  {data.bundle_products.map((bi, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {bi.product?.images?.[0] && (
                        <img src={bi.product.images[0]} alt={bi.product.name} className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="text-gray-700">{bi.product?.name}</span>
                      {bi.quantity > 1 && <span className="text-xs text-gray-400">x{bi.quantity}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Product meta */}
              <div className="flex flex-wrap gap-2">
                {!bundle && !isWebinar && product?.stock > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                    <CircleDot className="h-3 w-3" /> Stok Tersedia
                  </span>
                )}
                {bundle && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                    <Package className="h-3 w-3" /> {data?.bundle_products?.length || bundle.items?.length || 0} Produk
                  </span>
                )}
                {isPhysical && product?.weight && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                    <Package className="h-3 w-3" /> {product.weight >= 1000 ? `${(product.weight / 1000).toFixed(1)} kg` : `${product.weight} gram`}
                  </span>
                )}
                {isWebinar && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                    <Video className="h-3 w-3" /> Webinar Online
                  </span>
                )}
                {isEvent && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                    <Calendar className="h-3 w-3" /> Event
                  </span>
                )}
                {isBooking && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <Calendar className="h-3 w-3" /> Booking
                  </span>
                )}
              </div>

              {/* Quantity selector - not for webinar/event/booking */}
              {page.enable_quantity && !isWebinar && !isEvent && !isBooking && (
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                  <span className="text-sm font-medium text-gray-700">Jumlah:</span>
                  <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
                    <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition text-gray-600 disabled:opacity-30"
                      disabled={quantity <= 1}>
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-12 text-center font-bold text-gray-900">{quantity}</span>
                    <button type="button" onClick={() => setQuantity(q => Math.min(product?.stock || 999, q + 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition text-gray-600">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {quantity > 1 && (
                    <span className="text-sm font-semibold" style={{ color }}>Rp {fmt(subtotal)}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ═══════ TESTIMONIALS ═══════ */}
        {page.testimonials?.length > 0 && (
          <section className="mt-8">
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 mb-3">
                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" /> Testimoni Pelanggan
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Apa Kata Mereka?</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {page.testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  {/* Stars */}
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  {/* Text */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.text}"</p>
                  {/* Author */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    {t.avatar_url ? (
                      <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ring-2 ring-white shadow" style={{ backgroundColor: color }}>
                        {(t.name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{t.name}</p>
                      {t.role && <p className="text-xs text-gray-500">{t.role}</p>}
                    </div>
                    <BadgeCheck className="h-4 w-4 text-blue-500 ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ═══════ CUSTOM HTML: before_checkout ═══════ */}
        {(page.custom_html_blocks || []).filter(b => b.position === 'before_checkout' && b.html).map((b, i) => (
          <section key={`html-bc-${i}`} className="mt-6" dangerouslySetInnerHTML={{ __html: b.html }} />
        ))}

        {/* ═══════ FAQ SECTION ═══════ */}
        {page.faq_items?.length > 0 && (
          <section className="mt-8">
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 mb-3">
                <HelpCircle className="h-3.5 w-3.5" /> FAQ
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Pertanyaan yang Sering Ditanyakan</h2>
            </div>
            <div className="space-y-2">
              {page.faq_items.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-sm text-gray-800 pr-4">{faq.question}</span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ═══════ GUARANTEE SECTION ═══════ */}
        {page.guarantee_days > 0 && (
          <section className="mt-8">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-200 p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-emerald-800 mb-2">
                Garansi {page.guarantee_days} Hari Uang Kembali
              </h3>
              {page.guarantee_text && (
                <p className="text-sm text-emerald-700 max-w-md mx-auto leading-relaxed">
                  {page.guarantee_text}
                </p>
              )}
              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-emerald-600">
                <Shield className="h-3.5 w-3.5" /> 100% Aman &nbsp;•&nbsp; Tanpa Risiko
              </div>
            </div>
          </section>
        )}

        {/* ═══════ CHECKOUT FORM ═══════ */}
        {isWebinar ? (
          /* ── WEBINAR REGISTRATION FORM ── */
          <section className="mt-10" ref={formRef}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 pt-6 pb-4 border-b border-gray-100" style={{ background: `linear-gradient(180deg, ${color}08 0%, transparent 100%)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-violet-600">
                    <Video className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Daftar Webinar</h2>
                    <p className="text-xs text-gray-500">Isi data di bawah untuk mendaftar webinar</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleWebinarRegister} className="p-6 space-y-5">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-violet-600">1</div>
                    Data Peserta
                  </div>
                  <div className="space-y-3 pl-8">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <User className="h-3.5 w-3.5 text-gray-400" /> Nama Lengkap <span className="text-red-500">*</span>
                      </Label>
                      <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                        placeholder="Masukkan nama lengkap Anda" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Mail className="h-3.5 w-3.5 text-gray-400" /> Email <span className="text-red-500">*</span>
                      </Label>
                      <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                        placeholder="email@contoh.com" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-400" /> No. HP / WhatsApp
                      </Label>
                      <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx" className="h-11" />
                    </div>
                  </div>
                </div>

                {/* Payment method - only for paid webinars */}
                {displayPrice > 0 && (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-violet-600">2</div>
                      Metode Pembayaran
                    </div>
                    <div className="space-y-2 pl-8">
                      <RadioGroup value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                        {[
                          { value: 'qris', label: 'QRIS', desc: 'Scan QR dari e-wallet manapun' },
                          { value: 'va', label: 'Virtual Account', desc: 'Transfer via ATM/mobile banking' },
                          { value: 'ewallet', label: 'E-Wallet', desc: 'GoPay, OVO, DANA, dll' },
                          { value: 'bank_transfer', label: 'Transfer Bank', desc: 'Transfer manual ke rekening' },
                        ].map(pm => (
                          <label key={pm.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${form.payment_method === pm.value ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                            <RadioGroupItem value={pm.value} className="sr-only" />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.payment_method === pm.value ? 'border-violet-500' : 'border-gray-300'}`}>
                              {form.payment_method === pm.value && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{pm.label}</p>
                              <p className="text-xs text-gray-500">{pm.desc}</p>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{displayName}</p>
                      <p className="text-xs text-gray-500">
                        {webinarData?.start_time && new Date(webinarData.start_time).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <p className="text-lg font-bold" style={{ color }}>
                      {displayPrice === 0 ? 'Gratis' : `Rp ${fmt(displayPrice)}`}
                    </p>
                  </div>
                </div>

                {/* Submit */}
                <Button type="submit" disabled={webinarRegistering}
                  className="w-full h-14 text-base font-bold rounded-xl shadow-lg text-white transition-all hover:scale-[1.01]"
                  style={{ backgroundColor: color }}>
                  {webinarRegistering ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Video className="h-5 w-5 mr-2" />
                  )}
                  {webinarRegistering ? 'Mendaftar...' : (displayPrice > 0 ? 'Daftar & Bayar Sekarang' : 'Daftar Gratis Sekarang')}
                </Button>

                <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400 pt-1">
                  <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Data Aman</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Konfirmasi Instan</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Terpercaya</span>
                </div>
              </form>
            </div>
          </section>
        ) : isEvent ? (
          /* ── EVENT REGISTRATION FORM ── */
          <section className="mt-10" ref={formRef}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 pt-6 pb-4 border-b border-gray-100" style={{ background: `linear-gradient(180deg, ${color}08 0%, transparent 100%)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-violet-600">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Daftar Event</h2>
                    <p className="text-xs text-gray-500">Isi data untuk mendaftar event ini</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleEventRegister} className="p-6 space-y-5">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-violet-600">1</div>
                    Data Peserta
                  </div>
                  <div className="space-y-3 pl-8">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <User className="h-3.5 w-3.5 text-gray-400" /> Nama Lengkap <span className="text-red-500">*</span>
                      </Label>
                      <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                        placeholder="Masukkan nama lengkap Anda" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Mail className="h-3.5 w-3.5 text-gray-400" /> Email <span className="text-red-500">*</span>
                      </Label>
                      <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                        placeholder="email@contoh.com" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-400" /> No. HP / WhatsApp
                      </Label>
                      <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx" className="h-11" />
                    </div>
                  </div>
                </div>

                {/* Ticket selection reminder */}
                {!selectedTicketType && eventTicketTypes.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Pilih tipe tiket di bagian atas terlebih dahulu sebelum mendaftar.</span>
                  </div>
                )}

                {/* Payment method - only for paid events */}
                {selectedTicketType && selectedTicketType.price > 0 && (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-violet-600">2</div>
                      Metode Pembayaran
                    </div>
                    <div className="space-y-2 pl-8">
                      <RadioGroup value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                        {[
                          { value: 'qris', label: 'QRIS', desc: 'Scan QR dari e-wallet manapun' },
                          { value: 'va', label: 'Virtual Account', desc: 'Transfer via ATM/mobile banking' },
                          { value: 'ewallet', label: 'E-Wallet', desc: 'GoPay, OVO, DANA, dll' },
                          { value: 'bank_transfer', label: 'Transfer Bank', desc: 'Transfer manual ke rekening' },
                        ].map(pm => (
                          <label key={pm.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${form.payment_method === pm.value ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                            <RadioGroupItem value={pm.value} className="sr-only" />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.payment_method === pm.value ? 'border-violet-500' : 'border-gray-300'}`}>
                              {form.payment_method === pm.value && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{pm.label}</p>
                              <p className="text-xs text-gray-500">{pm.desc}</p>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{displayName}</p>
                      {selectedTicketType && <p className="text-xs text-gray-500">{selectedTicketType.name} x{eventQuantity}</p>}
                      {eventData?.start_date && <p className="text-xs text-gray-500">{new Date(eventData.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                    </div>
                    <p className="text-lg font-bold" style={{ color }}>
                      {(!selectedTicketType || selectedTicketType.price === 0) ? 'Gratis' : `Rp ${fmt(selectedTicketType.price * eventQuantity)}`}
                    </p>
                  </div>
                </div>

                {/* Submit */}
                <Button type="submit" disabled={eventRegistering || !selectedTicketType}
                  className="w-full h-14 text-base font-bold rounded-xl shadow-lg text-white transition-all hover:scale-[1.01]"
                  style={{ backgroundColor: color }}>
                  {eventRegistering ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Calendar className="h-5 w-5 mr-2" />
                  )}
                  {eventRegistering ? 'Mendaftar...' : (selectedTicketType?.price > 0 ? 'Daftar & Bayar Sekarang' : 'Daftar Gratis Sekarang')}
                </Button>

                <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400 pt-1">
                  <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Data Aman</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Konfirmasi Instan</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Terpercaya</span>
                </div>
              </form>
            </div>
          </section>
        ) : isBooking ? (
          /* ── BOOKING FORM ── */
          <section className="mt-10" ref={formRef}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 pt-6 pb-4 border-b border-gray-100" style={{ background: `linear-gradient(180deg, ${color}08 0%, transparent 100%)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-blue-600">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Booking Sekarang</h2>
                    <p className="text-xs text-gray-500">Isi data untuk melakukan booking</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleBookingSubmit} className="p-6 space-y-5">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-blue-600">1</div>
                    Data Pemesan
                  </div>
                  <div className="space-y-3 pl-8">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <User className="h-3.5 w-3.5 text-gray-400" /> Nama Lengkap <span className="text-red-500">*</span>
                      </Label>
                      <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                        placeholder="Masukkan nama lengkap Anda" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Mail className="h-3.5 w-3.5 text-gray-400" /> Email <span className="text-red-500">*</span>
                      </Label>
                      <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                        placeholder="email@contoh.com" required className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-400" /> No. HP / WhatsApp
                      </Label>
                      <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx" className="h-11" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <FileText className="h-3.5 w-3.5 text-gray-400" /> Catatan (opsional)
                      </Label>
                      <Textarea rows={2} value={bookingNotes} onChange={e => setBookingNotes(e.target.value)}
                        placeholder="Catatan atau permintaan khusus" />
                    </div>
                  </div>
                </div>

                {/* Date/time selection reminder */}
                {!selectedDate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Pilih tanggal booking di bagian atas terlebih dahulu.</span>
                  </div>
                )}

                {/* Payment method - only for paid bookings */}
                {bookingService?.price > 0 && (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold bg-blue-600">2</div>
                      Metode Pembayaran
                    </div>
                    <div className="space-y-2 pl-8">
                      <RadioGroup value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                        {[
                          { value: 'qris', label: 'QRIS', desc: 'Scan QR dari e-wallet manapun' },
                          { value: 'va', label: 'Virtual Account', desc: 'Transfer via ATM/mobile banking' },
                          { value: 'ewallet', label: 'E-Wallet', desc: 'GoPay, OVO, DANA, dll' },
                          { value: 'bank_transfer', label: 'Transfer Bank', desc: 'Transfer manual ke rekening' },
                        ].map(pm => (
                          <label key={pm.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${form.payment_method === pm.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                            <RadioGroupItem value={pm.value} className="sr-only" />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.payment_method === pm.value ? 'border-blue-500' : 'border-gray-300'}`}>
                              {form.payment_method === pm.value && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{pm.label}</p>
                              <p className="text-xs text-gray-500">{pm.desc}</p>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{displayName}</p>
                      {selectedDate && <p className="text-xs text-gray-500">{fmtDate(selectedDate)}</p>}
                      {bookingMode === 'daily' && bookingEndDate && bookingDays > 1 && (
                        <p className="text-xs text-gray-500">s/d {fmtDate(bookingEndDate)} ({bookingDays} hari)</p>
                      )}
                      {selectedSlot && <p className="text-xs text-gray-500">{selectedSlot.time_start} - {selectedSlot.time_end}</p>}
                      {bookingMode === 'daily' && bookingDays > 1 && bookingUnitPrice > 0 && (
                        <p className="text-xs text-gray-500">{bookingDays} hari × Rp {fmt(bookingUnitPrice)}</p>
                      )}
                      {bookingMode === 'quantity' && bookingQuantity > 1 && bookingUnitPrice > 0 && (
                        <p className="text-xs text-gray-500">{bookingQuantity} unit × Rp {fmt(bookingUnitPrice)}</p>
                      )}
                    </div>
                    <p className="text-lg font-bold" style={{ color }}>
                      {bookingTotalPrice === 0 ? 'Gratis' : `Rp ${fmt(bookingTotalPrice)}`}
                    </p>
                  </div>
                </div>

                {/* Submit */}
                <Button type="submit"
                  disabled={bookingSubmitting || !selectedDate || (['hourly', 'session'].includes(bookingMode) && !selectedSlot)}
                  className="w-full h-14 text-base font-bold rounded-xl shadow-lg text-white transition-all hover:scale-[1.01]"
                  style={{ backgroundColor: color }}>
                  {bookingSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Calendar className="h-5 w-5 mr-2" />
                  )}
                  {bookingSubmitting ? 'Memproses...' : (bookingService?.price > 0 ? 'Booking & Bayar Sekarang' : 'Booking Gratis Sekarang')}
                </Button>

                <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400 pt-1">
                  <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Data Aman</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Konfirmasi Instan</span>
                  <span>•</span>
                  <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Terpercaya</span>
                </div>
              </form>
            </div>
          </section>
        ) : (
        <section className="mt-10" ref={formRef}>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Form header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100" style={{ background: `linear-gradient(180deg, ${color}08 0%, transparent 100%)` }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: color }}>
                  <ShoppingBag className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">{page.form_title || 'Order Sekarang'}</h2>
                  <p className="text-xs text-gray-500">Isi data di bawah untuk melanjutkan pesanan</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* ── Customer Info ── */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold" style={{ backgroundColor: color }}>1</div>
                  Data Pemesan
                </div>

                <div className="space-y-3 pl-8">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                      <Users className="h-3.5 w-3.5 text-gray-400" /> Nama Lengkap <span className="text-red-500">*</span>
                    </Label>
                    <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                      placeholder="Masukkan nama lengkap Anda" required className="h-11" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                      <Mail className="h-3.5 w-3.5 text-gray-400" /> Email <span className="text-red-500">*</span>
                    </Label>
                    <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                      onBlur={fetchLoyaltyInfo} placeholder="email@contoh.com" required className="h-11" />
                  </div>
                  {page.show_phone !== false && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-400" /> No. HP / WhatsApp
                      </Label>
                      <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx" className="h-11" />
                    </div>
                  )}
                  {page.show_address !== false && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <MapPin className="h-3.5 w-3.5 text-gray-400" /> Alamat Pengiriman
                      </Label>
                      <Textarea rows={2} value={form.customer_address}
                        onChange={e => setForm(f => ({ ...f, customer_address: e.target.value }))}
                        placeholder="Alamat lengkap (termasuk RT/RW, kelurahan, kecamatan)" />
                    </div>
                  )}
                  {page.show_notes && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <FileText className="h-3.5 w-3.5 text-gray-400" /> Catatan
                      </Label>
                      <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Catatan tambahan (opsional)" />
                    </div>
                  )}

                  {/* Affiliate / Referral Code */}
                  {page.enable_affiliate !== false && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                        <Users className="h-3.5 w-3.5 text-gray-400" /> Kode Referral
                      </Label>
                      <Input value={form.affiliate_code} onChange={e => {
                        const code = e.target.value;
                        setForm(f => ({ ...f, affiliate_code: code }));
                        setAffiliateValid(null); setAffiliateName('');
                        if (code.trim()) {
                          api.get(`/store/${subdomain}/validate-affiliate`, { params: { code: code.trim() } })
                            .then(res => {
                              if (res.data.valid) { setAffiliateName(res.data.name); setAffiliateValid(true); }
                              else setAffiliateValid(false);
                            })
                            .catch(() => setAffiliateValid(false));
                        }
                      }} placeholder="Kode affiliate (opsional)" className="h-11" />
                      {affiliateValid === true && affiliateName && (
                        <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                          <CheckCircle className="h-3.5 w-3.5" /> Direferensikan oleh: <strong>{affiliateName}</strong>
                        </p>
                      )}
                      {affiliateValid === false && form.affiliate_code && (
                        <p className="text-xs text-red-500 mt-1.5">Kode affiliate tidak valid</p>
                      )}
                    </div>
                  )}

                  {/* Reseller Code */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                      <Tag className="h-3.5 w-3.5 text-gray-400" /> Kode Reseller
                    </Label>
                    <Input value={form.reseller_code} onChange={e => {
                      const code = e.target.value.toUpperCase();
                      setForm(f => ({ ...f, reseller_code: code }));
                      setResellerPrices(null); setResellerTierName('');
                      if (code.trim().length >= 4) {
                        api.get(`/store/${subdomain}/reseller-prices`, { headers: { 'X-Reseller-Code': code } })
                          .then(res => { setResellerPrices(res.data.prices); setResellerTierName(res.data.tier_name || ''); })
                          .catch(() => { setResellerPrices(null); });
                      }
                    }} placeholder="Kode reseller (opsional)" className="h-11" />
                    {resellerTierName && (
                      <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                        <BadgeCheck className="h-3.5 w-3.5" /> Tier: <strong>{resellerTierName}</strong> — harga reseller akan diterapkan
                      </p>
                    )}
                  </div>

                  {/* Dropship Toggle */}
                  <div className="border border-gray-200 rounded-xl p-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_dropship} onChange={e => setForm(f => ({ ...f, is_dropship: e.target.checked }))} className="rounded" />
                      <span className="font-medium text-sm text-gray-700">Kirim sebagai Dropship</span>
                    </label>
                    {form.is_dropship && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-600">Nama Pengirim</Label>
                          <Input value={form.dropship_sender_name} onChange={e => setForm(f => ({ ...f, dropship_sender_name: e.target.value }))} placeholder="Nama toko / reseller" className="h-10" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">No. HP Pengirim</Label>
                          <Input value={form.dropship_sender_phone} onChange={e => setForm(f => ({ ...f, dropship_sender_phone: e.target.value }))} placeholder="08xxxxxxxxxx" className="h-10" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Shipping ── */}
              {isPhysical && shippingInfo?.has_shipping && (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold" style={{ backgroundColor: color }}>2</div>
                    Pengiriman
                  </div>

                  <div className="space-y-3 pl-8">
                    {shippingInfo.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min ? (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 font-medium">
                        <Gift className="h-4 w-4" /> Selamat! Anda mendapat <strong>GRATIS ONGKIR</strong>
                      </div>
                    ) : shippingInfo.flat_rate > 0 ? (
                      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
                        <Truck className="h-4 w-4" /> Ongkir flat rate: <strong>Rp {fmt(shippingInfo.flat_rate)}</strong>
                      </div>
                    ) : (shippingInfo.provider === 'rajaongkir' || shippingInfo.provider === 'komship') ? (
                      <>
                        {/* Destination search */}
                        <div>
                          <Label className="text-sm mb-1.5 block">Cari Kota / Kecamatan Tujuan</Label>
                          <div className="flex gap-2">
                            <Input value={destSearch} onChange={e => setDestSearch(e.target.value)} className="h-10"
                              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchDestination())}
                              placeholder="Ketik nama kota atau kecamatan..." />
                            <Button type="button" variant="outline" size="sm" onClick={searchDestination} disabled={destSearching} className="h-10 px-3">
                              {destSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>

                        {destResults.length > 0 && (
                          <div className="border rounded-xl max-h-40 overflow-y-auto bg-white text-sm shadow-sm">
                            {destResults.map((item, i) => {
                              const name = item.label || item.subdistrict_name || item.district_name || item.city_name || '';
                              const detail = item.label ? '' : `— ${item.district_name || item.city_name || ''}, ${item.province_name || item.province || ''}`;
                              return (
                                <button key={i} type="button" className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b last:border-0 transition-colors"
                                  onClick={() => { setSelectedDest(item); setDestResults([]); setDestSearch(''); }}>
                                  <span className="font-medium">{name}</span>
                                  {detail && <span className="text-gray-500 ml-1">{detail}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {selectedDest && (
                          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-xl border border-blue-200">
                            <MapPin className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">
                              Tujuan: <strong>{selectedDest.label || `${selectedDest.subdistrict_name || selectedDest.district_name || selectedDest.city_name}, ${selectedDest.province_name || selectedDest.province}`}</strong>
                            </span>
                            <button type="button" className="text-xs font-medium text-blue-600 hover:underline" onClick={() => { setSelectedDest(null); setShippingCosts([]); setSelectedShipping(null); }}>Ganti</button>
                          </div>
                        )}

                        {selectedDest && shippingInfo.enabled_couriers?.length > 0 && (
                          <div>
                            <Label className="text-sm mb-1.5 block">Pilih Kurir</Label>
                            <div className="flex flex-wrap gap-2">
                              {shippingInfo.enabled_couriers.map(c => (
                                <button key={c} type="button"
                                  className={`px-4 py-2 rounded-xl border text-sm font-semibold transition ${selectedCourier === c ? 'text-white shadow-md' : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'}`}
                                  style={selectedCourier === c ? { backgroundColor: color, borderColor: color } : {}}
                                  onClick={() => setSelectedCourier(c)}>
                                  {c.toUpperCase()}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {selectedDest && selectedCourier && (
                          <Button type="button" variant="outline" onClick={calculateShipping} disabled={shippingLoading} className="w-full h-10">
                            {shippingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
                            Hitung Ongkir
                          </Button>
                        )}

                        {shippingCosts.length > 0 && (
                          <RadioGroup value={selectedShipping?.service || ''} onValueChange={v => setSelectedShipping(shippingCosts.find(c => c.service === v))}>
                            <div className="space-y-2">
                              {shippingCosts.map((cost, idx) => (
                                <label key={idx} className={`flex items-center justify-between p-3.5 rounded-xl border-2 cursor-pointer text-sm transition ${selectedShipping?.service === cost.service ? 'shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                                  style={selectedShipping?.service === cost.service ? { borderColor: color, backgroundColor: `${color}08` } : {}}>
                                  <div className="flex items-center gap-2.5">
                                    <RadioGroupItem value={cost.service} />
                                    <div>
                                      <span className="font-semibold">{cost.courier?.toUpperCase()} — {cost.service}</span>
                                      <p className="text-xs text-gray-500">{cost.description} • Est. {cost.etd}</p>
                                      {cost.cod_available && (
                                        <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5">
                                          <BadgeCheck className="h-3 w-3" /> COD tersedia {cost.cod_fee > 0 ? `(+Rp ${fmt(cost.cod_fee)})` : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="font-bold whitespace-nowrap">Rp {fmt(cost.cost)}</span>
                                </label>
                              ))}
                            </div>
                          </RadioGroup>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3">Ongkir akan dihitung oleh penjual</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Payment Methods ── */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-bold" style={{ backgroundColor: color }}>
                    {isPhysical && shippingInfo?.has_shipping ? '3' : '2'}
                  </div>
                  Metode Pembayaran
                </div>

                <div className="pl-8 space-y-3">
                  {/* Payment tabs */}
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                    {[
                      { key: 'qris', label: 'QRIS', icon: QrCode },
                      { key: 'va', label: 'Transfer Bank', icon: Building },
                      { key: 'ewallet', label: 'E-Wallet', icon: Wallet },
                      { key: 'bank_transfer', label: 'Transfer Manual', icon: Landmark },
                      ...(isPhysical ? [{ key: 'cod', label: 'COD', icon: Truck }] : []),
                    ].map(tab => (
                      <button key={tab.key} type="button"
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition ${paymentTab === tab.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => {
                          setPaymentTab(tab.key);
                          if (tab.key === 'cod') {
                            setForm(f => ({ ...f, is_cod: true }));
                          } else {
                            const firstInGroup = PAYMENT_METHODS.find(p => p.group === tab.key);
                            if (firstInGroup) setForm(f => ({ ...f, is_cod: false, payment_method: firstInGroup.value }));
                          }
                        }}>
                        <tab.icon className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Payment options for current tab */}
                  <RadioGroup value={form.is_cod ? 'cod' : form.payment_method} onValueChange={v => {
                    if (v === 'cod') setForm(f => ({ ...f, is_cod: true }));
                    else setForm(f => ({ ...f, is_cod: false, payment_method: v }));
                  }}>
                    {paymentTab === 'cod' ? (
                      <div className="space-y-2">
                        <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${form.is_cod ? 'shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                          style={form.is_cod ? { borderColor: color, backgroundColor: `${color}08` } : {}}>
                          <RadioGroupItem value="cod" />
                          <div className="flex-1">
                            <p className="font-semibold text-sm">Bayar di Tempat (COD)</p>
                            <p className="text-xs text-gray-500">Bayar saat barang diterima oleh kurir</p>
                          </div>
                          <Truck className="h-5 w-5 text-gray-400" />
                        </label>
                        {form.is_cod && codFee > 0 && (
                          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                            <Info className="h-3.5 w-3.5" /> Biaya COD: <strong>Rp {fmt(codFee)}</strong>
                          </div>
                        )}
                        {form.is_cod && selectedShipping && selectedShipping.cod_available === false && (
                          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
                            <Info className="h-3.5 w-3.5" /> Kurir ini tidak mendukung COD. Silakan pilih kurir lain.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`grid gap-2 ${paymentTab === 'qris' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {PAYMENT_METHODS.filter(p => p.group === paymentTab).map(pm => {
                          const PMIcon = pm.icon;
                          const isActive = !form.is_cod && form.payment_method === pm.value;
                          return (
                            <label key={pm.value}
                              className={`flex items-center gap-3 border-2 rounded-xl p-3.5 cursor-pointer transition text-sm ${isActive ? 'shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                              style={isActive ? { borderColor: color, backgroundColor: `${color}08` } : {}}>
                              <RadioGroupItem value={pm.value} className="sr-only" />
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100">
                                <PMIcon className="h-4 w-4 text-gray-600" />
                              </div>
                              <div>
                                <p className="font-semibold">{pm.label}</p>
                                <p className="text-xs text-gray-500">{pm.desc}</p>
                              </div>
                              {isActive && <CheckCircle className="h-4 w-4 ml-auto" style={{ color }} />}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </RadioGroup>
                </div>
              </div>

              {/* ── Voucher Code ── */}
              {page.enable_voucher !== false && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Tag className="h-4 w-4 text-emerald-500" /> Kode Voucher
                  </div>
                  <div className="pl-0">
                    {voucherApplied ? (
                      <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <span className="font-semibold text-emerald-700 text-sm">{voucherApplied.code}</span>
                          <span className="text-sm text-emerald-600">— Diskon Rp {fmt(discount)}</span>
                        </div>
                        <button type="button" onClick={removeVoucher} className="text-xs font-medium text-red-500 hover:underline">Hapus</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input value={voucherInput} onChange={e => setVoucherInput(e.target.value)}
                          placeholder="Masukkan kode voucher" className="h-11 uppercase"
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), validateVoucher())} />
                        <Button type="button" onClick={validateVoucher} disabled={voucherLoading || !voucherInput.trim()}
                          className="h-11 px-5" style={{ backgroundColor: color }}>
                          {voucherLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Terapkan'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Loyalty Points ── */}
              {loyaltyInfo && loyaltyInfo.enabled && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Gift className="h-4 w-4 text-amber-500" /> Poin Loyalty
                  </div>
                  <p className="text-xs text-gray-500">
                    Saldo: <strong className="text-amber-600">{Number(loyaltyInfo.points).toLocaleString('id-ID')} poin</strong>
                  </p>
                  {loyaltyPointsUsed > 0 ? (
                    <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-amber-500" />
                        <span className="font-semibold text-amber-700 text-sm">{loyaltyPointsUsed} poin</span>
                        <span className="text-sm text-amber-600">— Diskon Rp {fmt(loyaltyDiscount)}</span>
                      </div>
                      <button type="button" onClick={removeLoyaltyPoints} className="text-xs font-medium text-red-500 hover:underline">Hapus</button>
                    </div>
                  ) : loyaltyInfo.points >= (loyaltyInfo.settings?.min_redeem_points || 10) ? (
                    <div className="flex gap-2">
                      <Input type="number" value={loyaltyPointsInput} onChange={e => setLoyaltyPointsInput(e.target.value)}
                        placeholder={`Min. ${loyaltyInfo.settings?.min_redeem_points || 10} poin`}
                        min={loyaltyInfo.settings?.min_redeem_points || 10} max={loyaltyInfo.points} className="h-11" />
                      <Button type="button" onClick={applyLoyaltyPoints} disabled={loyaltyLoading || !loyaltyPointsInput}
                        className="h-11 px-5 bg-amber-500 hover:bg-amber-600">
                        {loyaltyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Tukar'}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">Poin belum cukup (min. {loyaltyInfo.settings?.min_redeem_points || 10})</p>
                  )}
                </div>
              )}

              {/* ── Order Bumps ── */}
              {orderBumps.length > 0 && (
                <div className="pt-2 pb-1">
                  <div className="bg-amber-50 rounded-2xl p-5 border-2 border-dashed border-amber-300 space-y-3">
                    <h3 className="font-bold text-sm text-amber-800 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-amber-500" /> Penawaran Spesial
                    </h3>
                    {orderBumps.map(bump => {
                      const isSelected = selectedBumps.find(b => b.id === bump.id);
                      const bumpPrice = bump.bump_price ?? bump.product?.price ?? 0;
                      return (
                        <div
                          key={bump.id}
                          className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-amber-400 bg-white shadow-sm' : 'border-gray-200 bg-white/70 hover:border-amber-200'}`}
                          onClick={() => toggleBump(bump)}
                        >
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={!!isSelected} readOnly className="h-5 w-5 accent-amber-500 flex-shrink-0" />
                            <div className="w-11 h-11 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {(bump.image_url || bump.product?.images?.[0]) ? (
                                <img src={bump.image_url || bump.product?.images?.[0]} alt={bump.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="h-4 w-4 text-gray-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-amber-800">{bump.headline}</p>
                              <p className="text-xs text-gray-500">{bump.product?.name}</p>
                              {bump.description && <p className="text-xs text-gray-400 mt-0.5">{bump.description}</p>}
                            </div>
                            <span className="font-bold text-amber-600 text-sm whitespace-nowrap">+Rp {fmt(bumpPrice)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Bundle Suggestions ── */}
              {bundleSuggestions.length > 0 && (
                <div className="pt-2 pb-1">
                  <div className="bg-purple-50 rounded-2xl p-5 border-2 border-dashed border-purple-300 space-y-3">
                    <h3 className="font-bold text-sm text-purple-800 flex items-center gap-2">
                      <Package className="h-4 w-4 text-purple-500" /> Hemat dengan Bundle
                    </h3>
                    {bundleSuggestions.map(b => {
                      const isSelected = selectedBundleSugg.find(s => s.id === b.id);
                      const savings = b.original_total && b.final_price ? Math.round((1 - b.final_price / b.original_total) * 100) : 0;
                      return (
                        <div
                          key={b.id}
                          className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-purple-400 bg-white shadow-sm' : 'border-gray-200 bg-white/70 hover:border-purple-200'}`}
                          onClick={() => {
                            if (isSelected) setSelectedBundleSugg(selectedBundleSugg.filter(s => s.id !== b.id));
                            else setSelectedBundleSugg([...selectedBundleSugg, b]);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={!!isSelected} readOnly className="h-5 w-5 accent-purple-500 flex-shrink-0" />
                            <div className="w-11 h-11 bg-purple-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {b.images?.[0] ? (
                                <img src={b.images[0]} alt={b.name} className="w-full h-full object-cover" />
                              ) : (
                                <Package className="h-4 w-4 text-purple-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-purple-800">{b.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {b.original_total > b.final_price && (
                                  <span className="text-xs text-gray-400 line-through">Rp {fmt(b.original_total)}</span>
                                )}
                                <span className="text-xs font-bold text-purple-600">Rp {fmt(b.final_price)}</span>
                                {savings > 0 && <span className="text-xs bg-red-100 text-red-600 px-1 rounded">-{savings}%</span>}
                              </div>
                            </div>
                            <span className="font-bold text-purple-600 text-sm whitespace-nowrap">+Rp {fmt(b.final_price)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Order Summary ── */}
              <div className="pt-4">
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 space-y-3">
                  <h3 className="font-bold text-sm text-gray-800 flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Ringkasan Pesanan
                  </h3>

                  <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                    {displayImage && (
                      <img src={displayImage} alt={displayName} className="w-12 h-12 rounded-lg object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{displayName}</p>
                      {bundle && <p className="text-xs text-purple-500 font-medium">Bundle</p>}
                      <p className="text-xs text-gray-500">Qty: {quantity}</p>
                    </div>
                    <p className="text-sm font-semibold">Rp {fmt(subtotal)}</p>
                  </div>

                  {selectedBumps.map(bump => {
                    const bumpPrice = bump.bump_price ?? bump.product?.price ?? 0;
                    return (
                      <div key={bump.id} className="flex items-center gap-3 text-sm">
                        <Zap className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                        <span className="text-gray-600 flex-1 truncate">{bump.product?.name}</span>
                        <span className="font-semibold text-amber-600">+Rp {fmt(bumpPrice)}</span>
                      </div>
                    );
                  })}

                  {selectedBundleSugg.map(b => (
                    <div key={b.id} className="flex items-center gap-3 text-sm">
                      <Package className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                      <span className="text-gray-600 flex-1 truncate">{b.name}</span>
                      <span className="font-semibold text-purple-600">+Rp {fmt(b.final_price)}</span>
                    </div>
                  ))}

                  {shippingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5" />
                        Ongkir {selectedShipping ? `(${selectedShipping.courier?.toUpperCase()} ${selectedShipping.service})` : ''}
                      </span>
                      <span className="font-semibold">Rp {fmt(shippingCost)}</span>
                    </div>
                  )}
                  {isPhysical && shippingInfo?.free_shipping_min > 0 && subtotal >= shippingInfo.free_shipping_min && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                      <Gift className="h-3.5 w-3.5" /> Gratis ongkir!
                    </div>
                  )}
                  {codFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Biaya COD</span>
                      <span className="font-semibold">Rp {fmt(codFee)}</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span className="flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" />
                        Diskon{voucherApplied ? ` (${voucherApplied.code})` : ''}
                      </span>
                      <span className="font-semibold">-Rp {fmt(discount)}</span>
                    </div>
                  )}
                  {loyaltyDiscount > 0 && (
                    <div className="flex justify-between text-sm text-amber-600">
                      <span className="flex items-center gap-1.5">
                        <Gift className="h-3.5 w-3.5" />
                        Diskon Poin ({loyaltyPointsUsed} poin)
                      </span>
                      <span className="font-semibold">-Rp {fmt(loyaltyDiscount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="font-bold text-base text-gray-900">Total Pembayaran</span>
                    <span className="text-xl font-extrabold" style={{ color }}>Rp {fmt(total)}</span>
                  </div>
                </div>
              </div>

              {/* ── CTA Button ── */}
              <Button type="submit" disabled={submitting}
                className="w-full h-14 text-base font-bold rounded-xl shadow-lg transition-all hover:shadow-xl hover:scale-[1.01] active:scale-[0.99]"
                style={{ backgroundColor: color }}>
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <CtaIcon className="h-5 w-5 mr-2" />
                )}
                {submitting ? 'Memproses Pesanan...' : (page.button_text || 'Beli Sekarang')}
              </Button>

              {/* Trust info */}
              <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400 pt-1">
                <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Transaksi Aman</span>
                <span>•</span>
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Proses Cepat</span>
                <span>•</span>
                <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Terpercaya</span>
              </div>
            </form>
          </div>
        </section>
        )}

        {/* ═══════ CUSTOM HTML: after_checkout ═══════ */}
        {(page.custom_html_blocks || []).filter(b => b.position === 'after_checkout' && b.html).map((b, i) => (
          <section key={`html-ac-${i}`} className="mt-6" dangerouslySetInnerHTML={{ __html: b.html }} />
        ))}

        {/* ═══════ FOOTER ═══════ */}
        <footer className="mt-10 pb-6 text-center space-y-3">
          {page.footer_text && (
            <p className="text-sm text-gray-500">{page.footer_text}</p>
          )}
          <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Pembayaran Aman</span>
            <span>|</span>
            <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Verified Store</span>
          </div>
          {store?.name && (
            <p className="text-xs text-gray-400">© {new Date().getFullYear()} {store.name}. All rights reserved.</p>
          )}
          {/* Custom HTML: footer position */}
          {(page.custom_html_blocks || []).filter(b => b.position === 'footer' && b.html).map((b, i) => (
            <div key={`html-ft-${i}`} dangerouslySetInnerHTML={{ __html: b.html }} />
          ))}
        </footer>
      </div>

      {/* ═══════ FLOATING CTA BUTTON ═══════ */}
      {(page.enable_floating_cta !== false) && showFloatingCta && !success && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 truncate">{displayName}</p>
              <p className="font-extrabold text-sm" style={{ color }}>Rp {fmt(displayPrice)}</p>
            </div>
            <button onClick={scrollToForm}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex-shrink-0"
              style={{ backgroundColor: color }}>
              <CtaIcon className="h-4 w-4" />
              {page.button_text || 'Beli Sekarang'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
